import React, { useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faMagnifyingGlass, faHeart, faUser, faBasketShopping } from '@awesome.me/kit-367a27fd96/icons/classic/light';
import { useLogoutMutation, useWishlistQuery } from '../slices/usersApiSlice';
import { logout } from '../slices/authSlice';
import { resetCart } from '../slices/cartSlice';
import logo from '../assets/logo.png';
import SearchBox from './SearchBox';
import FilterDropdown from './FilterDropdown';

const MobileNavbar = () => {
    const [showFilterSidebar, setShowFilterSidebar] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [showAccountMenu, setShowAccountMenu] = useState(false);

    const { cartItems } = useSelector((state) => state.cart);
    const { userInfo } = useSelector((state) => state.auth);

    const { data: wishlist } = useWishlistQuery(userInfo?._id, {
        skip: !userInfo,
    });
    const wishlistCount = wishlist?.length || 0;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [logoutApiCall] = useLogoutMutation();

    const cartCount = cartItems.reduce((a, c) => a + c.qty, 0);

    const logoutHandler = async () => {
        try {
            await logoutApiCall().unwrap();
            dispatch(logout());
            dispatch(resetCart());
            setShowAccountMenu(false);
            navigate('/login');
        } catch (err) {
            console.log(err);
        }
    };

    const handleWishlistClick = () => {
        if (!userInfo) {
            navigate('/login');
        } else {
            navigate('/profile');
        }
    };

    const adminLinks = [
        { label: 'Orders', path: '/admin/orderlist' },
        { label: 'Products', path: '/admin/productlist' },
        { label: 'Users', path: '/admin/userlist' },
        { label: 'Promo Codes', path: '/admin/promocodelist' },
        { label: 'CSV Import', path: '/admin/faire-import' },
        { label: 'Stock Updates', path: '/admin/stock-updates' },
    ];

    return (
        <>
            <style>{`
                @media (max-width: 1199px) {
                    .mobile-navbar { padding: 0.75rem 1rem; }

                    .mobile-navbar-container {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 0.5rem;
                    }

                    .mobile-navbar-left,
                    .mobile-navbar-right {
                        display: flex;
                        align-items: center;
                        gap: 0;
                        flex: 0 0 auto;
                    }

                    .mobile-navbar-center {
                        flex: 1 1 0;
                        min-width: 0;
                        display: flex;
                        justify-content: center;
                    }

                    .mobile-navbar-center a { display: block; min-width: 0; }

                    /* The logo shrinks so all five icons always fit on screen */
                    .mobile-navbar-logo { display: block; height: auto; max-height: 50px; max-width: 100%; }

                    .mobile-nav-icon {
                        background: none;
                        border: none;
                        font-size: 1.2rem;
                        color: #000;
                        font-weight: 300;
                        cursor: pointer;
                        position: relative;
                        padding: 0.25rem;
                    }

                    .mobile-nav-badge {
                        position: absolute;
                        top: -5px;
                        right: -4px;
                        background: #FD7014;
                        color: white;
                        border-radius: 50%;
                        font-size: 0.7rem;
                        font-weight: bold;
                        min-width: 18px;
                        height: 18px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .mobile-search-dropdown {
                        background: white;
                        border-top: 1px solid #ddd;
                        padding: 1rem;
                        margin-top: 0.5rem;
                        position: relative;
                    }

                    .filter-sidebar .offcanvas { width: 85%; max-width: 350px; }
                    .filter-sidebar .offcanvas-header {
                        background: linear-gradient(135deg, #3E6D9C, #5a8ec4);
                        color: white;
                        border-bottom: 3px solid #FD7014;
                    }
                    .filter-sidebar .btn-close { filter: brightness(0) invert(1); }
                    .filter-sidebar .offcanvas-body { padding: 1.5rem; background: #f8f9fa; }

                    .account-menu-sidebar .offcanvas { width: 85%; max-width: 300px; }
                    .account-menu-sidebar .offcanvas-header {
                        background: #3E6D9C;
                        color: white;
                        border-bottom: 3px solid #FD7014;
                    }
                    .account-menu-sidebar .btn-close { filter: brightness(0) invert(1); }

                    .account-menu-item {
                        padding: 1rem;
                        border-bottom: 1px solid #e9ecef;
                        cursor: pointer;
                        transition: background 0.2s;
                        font-size: 0.95rem;
                        color: #212529;
                    }
                    .account-menu-item:hover { background: #f8f9fa; }

                    .account-menu-section-label {
                        padding: 0.6rem 1rem 0.4rem;
                        font-size: 0.7rem;
                        font-weight: 700;
                        letter-spacing: 1.5px;
                        text-transform: uppercase;
                        color: #fff;
                        background: #FD7014;
                    }

                    .account-menu-item-admin {
                        padding: 0.8rem 1rem 0.8rem 1.25rem;
                        border-bottom: 1px solid #e9ecef;
                        cursor: pointer;
                        transition: background 0.2s;
                        font-size: 0.9rem;
                        color: #3E6D9C;
                    }
                    .account-menu-item-admin:hover { background: #eef4fb; }
                }

                @media (min-width: 1200px) {
                    .mobile-navbar-wrapper { display: none; }
                }
            `}</style>

            <div className="mobile-navbar-wrapper">
                <div className="mobile-navbar">
                    <div className="mobile-navbar-container">
                        <div className="mobile-navbar-left">
                            <button className="mobile-nav-icon" onClick={() => setShowFilterSidebar(true)}>
                                <FontAwesomeIcon icon={faBars} />
                            </button>
                            <button className="mobile-nav-icon" onClick={() => setShowSearch(!showSearch)}>
                                <FontAwesomeIcon icon={faMagnifyingGlass} />
                            </button>
                        </div>

                        <div className="mobile-navbar-center">
                            <LinkContainer to="/">
                                <a><img src={logo} alt="RetroDayz" className="mobile-navbar-logo" /></a>
                            </LinkContainer>
                        </div>

                        <div className="mobile-navbar-right">
                            <button className="mobile-nav-icon" aria-label="Wishlist" onClick={handleWishlistClick}>
                                <FontAwesomeIcon icon={faHeart} />
                                {wishlistCount > 0 && <span className="mobile-nav-badge">{wishlistCount}</span>}
                            </button>

                            <button className="mobile-nav-icon" aria-label="Account" onClick={() => userInfo ? setShowAccountMenu(true) : navigate('/login')}>
                                <FontAwesomeIcon icon={faUser} />
                            </button>

                            <LinkContainer to="/cart">
                                <button className="mobile-nav-icon" aria-label={`Basket, ${cartCount} items`}>
                                    <FontAwesomeIcon icon={faBasketShopping} />
                                    {cartCount > 0 && <span className="mobile-nav-badge">{cartCount}</span>}
                                </button>
                            </LinkContainer>
                        </div>
                    </div>

                    {showSearch && (
                        <div className="mobile-search-dropdown">
                            <SearchBox />
                        </div>
                    )}
                </div>
            </div>

            {/* Filter sidebar */}
            <Offcanvas show={showFilterSidebar} onHide={() => setShowFilterSidebar(false)} placement="start" className="filter-sidebar">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Browse &amp; Filter</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <nav aria-label="Quick links" className="mobile-menu-links">
                        {[['All Products', '/search/'], ['New Arrivals', '/search?sort=newest'], ['Help', '/help'], ['Contact Us', '/contact']].map(([label, to]) => (
                            <Link key={to} to={to} onClick={() => setShowFilterSidebar(false)}>{label}</Link>
                        ))}
                    </nav>
                    <FilterDropdown defaultExpanded={true} />
                </Offcanvas.Body>
            </Offcanvas>

            {/* Account sidebar */}
            {userInfo && (
                <Offcanvas show={showAccountMenu} onHide={() => setShowAccountMenu(false)} placement="end" className="account-menu-sidebar">
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title>{userInfo.name}</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body style={{ padding: 0 }}>

                        {/* Account section */}
                        <div className="account-menu-section-label" style={{ background: '#3E6D9C' }}>
                            Account
                        </div>
                        <div className="account-menu-item" onClick={() => { navigate('/profile'); setShowAccountMenu(false); }}>
                            Profile
                        </div>
                        <div className="account-menu-item" onClick={() => { navigate('/orders'); setShowAccountMenu(false); }}>
                            Orders
                        </div>
                        <div className="account-menu-item" onClick={logoutHandler} style={{ color: '#dc3545' }}>
                            Logout
                        </div>

                        {/* Admin section — only visible to admins */}
                        {userInfo.isAdmin && (
                            <>
                                <div className="account-menu-section-label">
                                    Admin
                                </div>
                                {adminLinks.map(({ label, path }) => (
                                    <div
                                        key={path}
                                        className="account-menu-item-admin"
                                        onClick={() => { navigate(path); setShowAccountMenu(false); }}
                                    >
                                        {label}
                                    </div>
                                ))}
                            </>
                        )}

                    </Offcanvas.Body>
                </Offcanvas>
            )}
        </>
    );
};

export default MobileNavbar;